import { useState, useEffect } from 'react';
import { getSession, activateFarmBoost, spendTokens, saveSession } from '../utils/session';

function BoostShop({ session, onPurchaseComplete }) {
  const [purchasing, setPurchasing] = useState(null);
  const [message, setMessage] = useState('');
  const [currentTokens, setCurrentTokens] = useState(session?.tokens || 0);
  const [farmBoostRemaining, setFarmBoostRemaining] = useState(session?.farmBoost || 0);
  const [spotlightUsername, setSpotlightUsername] = useState('');

  // Update local state when session prop changes
  useEffect(() => {
    setCurrentTokens(session?.tokens || 0);
    setFarmBoostRemaining(session?.farmBoost || 0);
  }, [session]);

  const handleFarmBoost = () => {
    setPurchasing('farm');
    const success = activateFarmBoost();

    if (success) {
      // Get updated balance from session
      const sessionData = getSession();
      const newBalance = sessionData?.tokens || 0;

      // Immediately update local state for instant UI feedback
      setCurrentTokens(newBalance);
      setFarmBoostRemaining(20);
      setMessage('✅ Farm Boost activated! Next 20 swipes give 2x Aura!');

      // Update parent's live balance
      onPurchaseComplete(newBalance);
    } else {
      setMessage('❌ Not enough Lit-Bits! Need 20 Lit-Bits.');
    }

    setTimeout(() => {
      setMessage('');
      setPurchasing(null);
    }, 3000);
  };

  const handleSpotlight = async () => {
    setPurchasing('spotlight');

    if (!session || currentTokens < 30) {
      setMessage('❌ Not enough Lit-Bits! Need 30 Lit-Bits.');
      setTimeout(() => {
        setMessage('');
        setPurchasing(null);
      }, 3000);
      return;
    }

    // Check if username is entered
    if (!spotlightUsername.trim()) {
      setMessage('❌ Please enter a username to spotlight!');
      setTimeout(() => {
        setMessage('');
        setPurchasing(null);
      }, 3000);
      return;
    }

    try {
      // Look up the user to get their avatar ID
      const userResponse = await fetch(
        `${import.meta.env.VITE_API_URL || '/api'}/user/${encodeURIComponent(spotlightUsername)}`
      );

      if (!userResponse.ok) {
        if (userResponse.status === 404) {
          setMessage('❌ User not found! Make sure they have submitted their avatar.');
        } else {
          setMessage('❌ Failed to find user. Please try again.');
        }
        setTimeout(() => {
          setMessage('');
          setPurchasing(null);
        }, 3000);
        return;
      }

      const userData = await userResponse.json();
      const avatarId = userData.id;

      const spent = spendTokens(30);
      if (spent) {
        // Activate spotlight on backend
        const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/spotlight/activate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            avatarId: avatarId,
            duration: 30 * 60 * 1000, // 30 minutes
          }),
        });

        if (response.ok) {
          // Get updated balance from session
          const sessionData = getSession();
          const newBalance = sessionData?.tokens || 0;

          // Immediately update local state for instant UI feedback
          setCurrentTokens(newBalance);
          setMessage(`✅ Spotlight activated for ${userData.username}! Featured for 30 minutes!`);
          setSpotlightUsername(''); // Clear the input

          // Update parent's live balance
          onPurchaseComplete(newBalance);
        } else {
          spendTokens(-30); // Refund
          const sessionData = getSession();
          const newBalance = sessionData?.tokens || 0;
          setCurrentTokens(newBalance); // Update with refunded balance
          setMessage('❌ Failed to activate Spotlight. Please try again.');
        }
      } else {
        setMessage('❌ Failed to activate Spotlight.');
      }
    } catch (err) {
      setMessage('❌ Error activating Spotlight.');
      console.error('Spotlight error:', err);
    }

    setTimeout(() => {
      setMessage('');
      setPurchasing(null);
    }, 3000);
  };


  return (
    <div className="boost-shop-container">
      <div className="boost-shop-header">
        <h1 className="boost-shop-title">⚡ Power-Ups & Boosts ⚡</h1>
        <p className="boost-shop-subtitle">
          Supercharge your voting and farming with premium boosts!
        </p>
        <div className="current-tokens">
          You have <span className="token-count">{currentTokens}</span> Lit-Bits
        </div>
      </div>

      {message && (
        <div className={`boost-message ${message.includes('❌') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="boosts-grid">
        {/* Power Vote */}
        <div className="boost-card power-vote">
          <div className="boost-icon">💪</div>
          <h3 className="boost-name">Power Vote</h3>
          <div className="boost-cost">12 Lit-Bits per use</div>
          <p className="boost-description">
            Your vote counts <strong>3x</strong> more! Available as an option on every vote. Perfect for leaderboard wars and supporting your favorite avatars.
          </p>

          <div className="boost-features">
            <div className="feature">✓ Triple vote impact</div>
            <div className="feature">✓ Available on all voting pages</div>
            <div className="feature">✓ Pay-per-use (no activation needed)</div>
          </div>

          <div className="boost-info-note">
            ℹ️ Use Power Vote directly on any voting page - just click the "💪 Power Vote" button instead of regular vote!
          </div>
        </div>

        {/* Farm Boost */}
        <div className="boost-card farm-boost">
          <div className="boost-icon">🚀</div>
          <h3 className="boost-name">Farm Boost</h3>
          <div className="boost-cost">20 Lit-Bits</div>
          <p className="boost-description">
            Double the Aura effect on your next <strong>20 swipes</strong>! Perfect for climbing the leaderboard fast.
          </p>

          {farmBoostRemaining > 0 && (
            <div className="active-boost">
              🔥 Active: {farmBoostRemaining} swipes remaining
            </div>
          )}

          <div className="boost-features">
            <div className="feature">✓ 2x Aura per swipe</div>
            <div className="feature">✓ Stacks with regular swipes</div>
            <div className="feature">✓ Lasts 20 swipes</div>
          </div>

          <button
            className="boost-button farm"
            onClick={handleFarmBoost}
            disabled={purchasing === 'farm' || farmBoostRemaining > 0}
          >
            {purchasing === 'farm' ? 'Activating...' : farmBoostRemaining > 0 ? 'Already Active' : 'Activate Boost'}
          </button>
        </div>

        {/* Spotlight */}
        <div className="boost-card spotlight">
          <div className="boost-icon">⭐</div>
          <h3 className="boost-name">Spotlight</h3>
          <div className="boost-cost">30 Lit-Bits</div>
          <p className="boost-description">
            Feature any avatar in the <strong>Spotlight Feed</strong> for 30 minutes! Stackable - each purchase adds more time.
          </p>

          <div className="boost-features">
            <div className="feature">✓ Spotlight ANY user's avatar</div>
            <div className="feature">✓ 30 minutes per purchase</div>
            <div className="feature">✓ Stackable - add more time!</div>
          </div>

          <div className="spotlight-input-section">
            <label htmlFor="spotlight-username" className="spotlight-label">
              Username to Spotlight:
            </label>
            <input
              id="spotlight-username"
              type="text"
              className="spotlight-input"
              placeholder="Enter Roblox username..."
              value={spotlightUsername}
              onChange={(e) => setSpotlightUsername(e.target.value)}
              disabled={purchasing === 'spotlight'}
            />
          </div>

          <button
            className="boost-button spotlight-btn"
            onClick={handleSpotlight}
            disabled={purchasing === 'spotlight'}
          >
            {purchasing === 'spotlight' ? 'Activating...' : 'Activate Spotlight'}
          </button>
        </div>
      </div>

      <div className="boost-info">
        <h3>💡 Pro Tips</h3>
        <ul>
          <li><strong>Farm Boost:</strong> Use before a long farming session to maximize Aura gains</li>
          <li><strong>Spotlight:</strong> Feature anyone's avatar! Great for promoting friends or showing support</li>
          <li><strong>Power Vote:</strong> Use on the Vote page for 3x voting power</li>
        </ul>

        <div className="boost-faq">
          <h3>FAQ</h3>
          <div className="faq-item">
            <p className="faq-question">Do boosts stack?</p>
            <p className="faq-answer">
              Spotlight is stackable - each purchase adds 30 more minutes to your active time! Farm Boost can only have one active at a time. Different boost types can be active simultaneously.
            </p>
          </div>
          <div className="faq-item">
            <p className="faq-question">Can I spotlight someone else's avatar?</p>
            <p className="faq-answer">
              Yes! Enter any username to spotlight their avatar. This is perfect for supporting friends or promoting avatars you think deserve recognition.
            </p>
          </div>
          <div className="faq-item">
            <p className="faq-question">What happens if I close the app?</p>
            <p className="faq-answer">
              Farm Boost tracks swipes, so it continues when you return. Spotlight is time-based and continues counting down even when the app is closed.
            </p>
          </div>
          <div className="faq-item">
            <p className="faq-question">Can I refund a boost?</p>
            <p className="faq-answer">
              No, all boost purchases are final. For Spotlight, make sure the username is correct and the user has submitted their avatar before purchasing!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BoostShop;
